import Highcharts from 'highcharts';
import escape from 'lodash/escape';
import jQuery from 'jquery';
import isString from 'lodash/isString';
import makeMoment from '../common/moment/MomentFactory';
import Locale from '../common/localization/Locale';
import translate from '../common/localization/translate';
import CosPhiService from './CosPhiService';

function addXAxisTooltip(chartOptions, scope) {
  let tiptext = null;
  let xValue = null;

  if (typeof scope.x === 'string') {
    xValue = scope.x;
    tiptext = '<strong>' + escape(xValue);
  } else {
    const decimalPlaces = 2;
    xValue = Highcharts.numberFormat(scope.x, decimalPlaces);
    tiptext = '<strong>' + xValue + ' ' + chartOptions.xAxis.title.text + ' ';
  }
  return tiptext + '</strong><br/>';
}

function getPointColor(point) {
  if (isString(point.series.color)) {
    return point.series.color;
  }
  try {
    return point.series.color.pattern.color;
  } catch (e) {
    return false;
  }
}

function addAreaRangePoint(point, tiptext) {
  const low = Highcharts.numberFormat(point.point.low, point.series.options.tooltip.decimals);
  const high = Highcharts.numberFormat(point.point.high, point.series.options.tooltip.decimals);
  tiptext += `<span class="mt-1" style="width: 11px; height: 11px; background-color: ${getPointColor(point)}; display: inline-block;"></span>
                <span>${point.series.name}</span>:
                <span style="font-weight: bold;">${low}</span> ${point.series.options.unit} –
                <span style="font-weight: bold;">${high}</span> ${point.series.options.unit}</span><br/>`;
  return tiptext;
}

function getPreviousYearPoint(point, points, response, key) {
  if (response && response.period === 'jahr') {
    const responseYear = parseInt(response.datetime, 10);
    const pointYear = parseInt((point.series.name.split(' ').slice(-1))[0], 10);
    const previousYearLabel = point.series.name.split(' ').slice(0, -1).join(' ') + ' ' + (pointYear - 1);
    if (key === 'L6P2F' && (point.series.name.includes('>') || point.series.name.includes('<'))) {
      const previousYearPoint = points.filter(function (entry) {
        if (pointYear === responseYear) {
          return entry.series.name.includes('>') || entry.series.name.includes('<');
        }
        return false;
      });
      return previousYearPoint.filter(function (entry) {
        return entry !== point;
      })[0];
    }
    const previousYearPoint = points.filter(function (entry) {
      return entry.series.name === previousYearLabel;
    });
    if (previousYearPoint.length > 0) {
      return previousYearPoint[0];
    }
  }
  return false;
}

function getCategoryName(point, key) {
  if (key === 'L6P2F') {
    if (point.series.name.includes('>')) {
      return point.series.name.split('>')[0].trim();
    }
    if (point.series.name.includes('<')) {
      return point.series.name.split('<')[0].trim();
    }
  }
  return point.series.name.split(' ').slice(0, -1).join(' ');
}

function addPoint(point, points, tiptext, response, key) {
  if (key === 'VGLV1' || key === 'C6DI3') {
    point.series.color = point.color;
  }
  const formatter = point.series.userOptions.isCosPhi
    ? CosPhiService.formatCosPhi
    : Highcharts.numberFormat;
  const value = formatter(point.y, point.series.options.tooltip.decimals);
  const previousYearPoint = getPreviousYearPoint(point, points, response, key);
  if (point.y === undefined) {
    tiptext += `${translate('javascript.TX_NO_DATA')}<br/>`;
  } else if (previousYearPoint) {
    tiptext += `<span class="mt-1" style="width: 11px; height: 11px; background-color: ${getPointColor(point)}; display: inline-block;"></span>
                <span>${point.series.name}</span>:
                <span style="font-weight: bold">${value}</span> ${point.series.options.unit}<br/>
                <span style="color: ${getPointColor(point)}">${getCategoryName(point, key)} ${translate('common.DEVIATION').toLowerCase()}</span>: 
                ${Math.round(((point.y - previousYearPoint.y) / point.y) * 10000) / 100} %<br/>`;
  } else {
    tiptext += `<span class="mt-1" style="width: 11px; height: 11px; background-color: ${getPointColor(point)}; display: inline-block;"></span>
                <span>${point.series.name}</span>:
                <span style="font-weight: bold">${value}</span> ${point.series.options.unit}<br/>`;
  }
  return tiptext;
}

function addPointsToTooltip(points, response, key) {
  let tiptext = '';
  jQuery.each(points, function (i, point) {
    if (point.series.options.tooltip) {
      const isAreaRangePoint = point.point.low !== undefined && point.point.high !== undefined;
      if (isAreaRangePoint) {
        tiptext = addAreaRangePoint(point, tiptext);
      } else {
        tiptext = addPoint(point, points, tiptext, response, key);
      }
    }
  });
  return tiptext;
}

function addYAxisTooltip(
  scope,
  chartSeries,
  optionalEfficiencyText = null,
  response = null,
  key = null,
) {
  const mainSeries = scope.series || scope.points[0].series;
  let tiptext = '';
  let value = null;

  if (scope.points) {
    return tiptext + addPointsToTooltip(scope.points, response, key);
  }

  if (typeof chartSeries[scope.series.index].tooltip.decimals !== 'undefined') {
    value = Highcharts.numberFormat(scope.y, chartSeries[scope.series.index].tooltip.decimals);
  } else {
    value = Highcharts.numberFormat(scope.y);
  }

  if (optionalEfficiencyText != null) {
    tiptext += `<span style="color:${mainSeries.color}">${optionalEfficiencyText}</span>: `;
  } else {
    tiptext += '<span style="color:' + mainSeries.color + '">' + mainSeries.name + '</span>: ';
  }

  return tiptext + '<span style="font-weight: bold">' + value + ' ' + mainSeries.yAxis.options.title.text + '</span><br/>';
}

function defaultFormatter(chartOptions, scope) {
  const tiptext = addXAxisTooltip(chartOptions, scope);
  return tiptext + addYAxisTooltip(scope, chartOptions.series);
}

function pieChartFormatter(scope, chartSeriesScope, response) {
  const roundedPercentage = Highcharts.numberFormat(
    scope.percentage,
    chartSeriesScope.tooltip.decimals,
  );

  let displayDate = '';
  if (response.period === 'monat' || response.period === 'jahr') {
    const selectedDate = new Date(response.datetime);
    const utc = Date.UTC(selectedDate.getFullYear(), selectedDate.getMonth());
    const momentDate = makeMoment(utc);
    momentDate.locale(Locale.getLanguageCode());

    displayDate = response.period === 'monat'
      ? momentDate.format('MMMM YYYY') : momentDate.format('YYYY');
  } else {
    displayDate = response.datetimeLoc;
  }

  const tiptext = `${displayDate} <br/>
    <span class="mt-1" style="width: 11px; height: 11px; background-color: ${scope.point.color}; display: inline-block;"></span>
    ${scope.point.name}:`;

  return `${tiptext} <strong>${roundedPercentage}</strong>%`;
}

function extractTooltipPointAndSeries(scope) {
  if (scope.points !== undefined) {
    return { point: scope.points[0].point, series: scope.points[0].series };
  }
  const { point, series } = scope;
  return { point, series };
}

function heatMapFormatter(scope, chartKey, response, valueSuffix) {
  const { point, series } = extractTooltipPointAndSeries(scope);

  const isValidValue = point.value !== undefined && point.value !== null && point.value !== '';
  const valueRepresentation = isValidValue ? `<b>${Highcharts.numberFormat(point.value, 2)}</b> ${valueSuffix}` : '<b>n/a</b>';
  let dateTimeFormat = 'L LT';
  if (response.period === 'jahr') {
    dateTimeFormat = 'MMM';
  }
  if (response.period === 'monat') {
    dateTimeFormat = 'DD';
  }
  return `<b>${makeMoment.utc(point.x).format(dateTimeFormat)}</b>`
    + '<br>' + series.yAxis.categories[point.y] + ' ' + valueRepresentation;
}

function systemEfficiencyFormatter(scope, chartOptions) {
  const { point, series } = extractTooltipPointAndSeries(scope);
  let tiptext = `<b>${makeMoment(point.timestamp).format('L LT')}</b>`;
  tiptext += `<br> <span style="color: ${series.color};">&#9724</span> ` + translate('evaluation.SYSTEM_EFFICIENCY') + ` <b>${Highcharts.numberFormat(scope.y)}</b> %`;
  tiptext += `<br> <span>${translate('common.ENERGY')}: </span>`;

  const decimals = 2;
  const displayedEnergy = `<b>${Highcharts.numberFormat(point.yield, decimals)}</b> ${translate('javascript.TX_KWH')}`;
  tiptext += displayedEnergy;
  tiptext += ` <br> <span>${translate('common.SENSOR_BASED_IRRADIANCE')}: </span>`;
  const xValue = Highcharts.numberFormat(scope.x, decimals);
  tiptext += '<b>' + xValue + '</b> ' + chartOptions.xAxis.title.text;
  return tiptext;
}

function inputOutputFormatter(scope, chartOptions) {
  const xValue = Highcharts.numberFormat(scope.x, 2);
  const yValue = Highcharts.numberFormat(scope.y, 2);
  const { series } = extractTooltipPointAndSeries(scope);

  return `<span style="font-weight: bold; color: ${series.color};">${series.name}</span>
      <br/>
      ${translate('common.TX_IRRADIATION')}: <span style="font-weight: bold;">${xValue}</span> ${chartOptions.xAxis.title.text}
      <br/>
      ${translate('common.TX_POWER')}: <span style="font-weight: bold;">${yValue}</span> ${chartOptions.yAxis[0].title.text}`;
}

function waterfallFormatter(scope) {
  const { point, series } = extractTooltipPointAndSeries(scope);
  const value = Highcharts.numberFormat(Math.abs(point.y), 2);
  return `<span>${point.name}</span>
      <br/>
      <span class="font-weight-bold">${value}</span> ${series.userOptions.unit}`;
}

function dateTimeFormatter(chartSeries, scope, response, key) {
  const mainSeries = scope.series || scope.points[0].series;
  const chartSeriesScope = scope.series ? chartSeries[scope.series.index] : {};

  let displayDate = scope.x;
  let displayTime = false;

  if (chartSeriesScope.type === 'pie') {
    return pieChartFormatter(scope, chartSeriesScope, response);
  }

  if (mainSeries.xAxis.options.type === 'datetime') {
    displayDate = makeMoment.utc(scope.x).format('L');
    displayTime = makeMoment.utc(scope.x).format('LT');
  } else if (response.period === 'monat' && mainSeries.type !== 'scatter') {
    const selectedDate = makeMoment.utc(response.datetime).date(scope.x);
    displayDate = !selectedDate.isValid() ? scope.x : selectedDate.format('L');
  }

  let tiptext = '<div>' + displayDate + ' ';
  if (displayTime) {
    tiptext += displayTime;
  }
  tiptext += '</div>';
  return tiptext + addYAxisTooltip(scope, chartSeries, null, response, key);
}

function inverterPrFormatter(chartOptions, scope, key) {
  const tiptext = addXAxisTooltip(chartOptions, scope);
  return tiptext + addYAxisTooltip(scope, chartOptions.series, null, null, key);
}

const TooltipService = {
  dateTimeFormatter,
  defaultFormatter,
  heatMapFormatter,
  inputOutputFormatter,
  systemEfficiencyFormatter,
  waterfallFormatter,
  inverterPrFormatter,
};

export default TooltipService;
